import { CustomFormSection } from "./types";
import { Col, Form, Flex, Row, Select, Switch, Tag, Typography } from "antd";
import { TicketData } from "../../models/event";
import { renderField } from "./renderField";

const { Text } = Typography;

const SectionHeader = ({
  section,
  tickets,
}: {
  section: CustomFormSection;
  tickets: TicketData[];
}) => {
  if (section.parentControlled) {
    return (
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const isEnabled = !!getFieldValue([section.id, "enabled"]);
          const isRequired = !!getFieldValue([section.id, "is_required"]);

          return (
            <div
              style={{ width: "100%", padding: "12px 16px" }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Row gutter={[12, 10]} align="middle">
                <Col xs={24} md={8}>
                  <Flex align="center" gap={8} wrap="wrap">
                    <Text
                      strong
                      style={{
                        fontSize: 14,
                        color: isEnabled ? "#1f2937" : "#9ca3af",
                      }}
                    >
                      {section.title}
                    </Text>
                    {isEnabled && isRequired && (
                      <Tag
                        color="red"
                        style={{
                          margin: 0,
                          fontSize: 10,
                          lineHeight: "18px",
                          paddingInline: 6,
                        }}
                      >
                        Mandatory
                      </Tag>
                    )}
                  </Flex>
                </Col>

                <Col xs={24} md={14}>
                  {isEnabled ? (
                    <Flex gap={10} align="center" wrap="wrap">
                      <Flex align="center" gap={6}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Required
                        </Text>
                        <Form.Item
                          name={[section.id, "is_required"]}
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch size="small" />
                        </Form.Item>
                      </Flex>

                      <Form.Item
                        name={[section.id, "enabled_for_categories"]}
                        rules={[
                          {
                            required: true,
                            message: "Please select at least one ticket",
                          },
                        ]}
                        noStyle
                      >
                        <Select
                          mode="multiple"
                          size="small"
                          placeholder="Tickets"
                          maxTagCount="responsive"
                          style={{ minWidth: 180 }}
                          options={tickets.map((ticket) => ({
                            label: ticket.name,
                            value: ticket.id ?? ticket.name,
                          }))}
                        />
                      </Form.Item>
                    </Flex>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Section disabled — all fields in this block are off
                    </Text>
                  )}
                </Col>

                <Col xs={24} md={2}>
                  <Flex justify="end">
                    <Form.Item
                      name={[section.id, "enabled"]}
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch size="small" />
                    </Form.Item>
                  </Flex>
                </Col>
              </Row>
            </div>
          );
        }}
      </Form.Item>
    );
  }

  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) => {
        const isEnabled =
          getFieldValue([section.id, "fields"])?.some((f: any) => f?.enabled) ??
          false;

        return (
          <div style={{ width: "100%", padding: "12px 16px" }}>
            <Flex justify="space-between" align="center" gap={12}>
              <Flex gap={8} align="center" style={{ flex: 1, minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <Text
                    strong
                    style={{
                      fontSize: 14,
                      color: isEnabled ? "#1f2937" : "#9ca3af",
                      display: "block",
                    }}
                  >
                    {section.title}
                  </Text>
                </div>
              </Flex>
            </Flex>
          </div>
        );
      }}
    </Form.Item>
  );
};

const SectionContent = ({
  section,
  tickets,
}: {
  section: CustomFormSection;
  tickets: TicketData[];
}) => {
  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) => {
        const isSectionEnabled = section.parentControlled
          ? !!getFieldValue([section.id, "enabled"])
          : true;

        return (
          <div style={{ padding: "8px 0 4px", borderTop: "1px solid #f3f4f6" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {section.fields.map((field, idx) =>
                renderField(
                  field,
                  section.id,
                  idx,
                  tickets,
                  section.parentControlled,
                  isSectionEnabled,
                ),
              )}
            </div>
          </div>
        );
      }}
    </Form.Item>
  );
};

export const getSectionItems = (
  sections: CustomFormSection[],
  tickets: TicketData[],
) => {
  return sections.map((section) => ({
    key: section.id,
    label: <SectionHeader section={section} tickets={tickets} />,
    children: <SectionContent section={section} tickets={tickets} />,
    forceRender: true,
    style: {
      border: "1px solid #e5e7eb",
      marginBottom: 8,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: "#ffffff",
    },
    className: "custom-section-item",
  }));
};
